import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Upload,
  Globe,
  ExternalLink,
  Loader2,
  XCircle,
  Rocket,
  Lock,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { CustomDomainModal } from "@/components/custom-domains/CustomDomainModal";
import { PublishRequiredModal } from "@/components/editor/PublishRequiredModal";
import { PublishVisibilityModal } from "@/components/editor/PublishVisibilityModal";
import { CommitBeforePublishModal } from "@/components/editor/CommitBeforePublishModal";
import { OptiSignsApiKeyModal } from "@/components/editor/OptiSignsApiKeyModal";
import { UnauthorizedApiKeyModal } from "@/components/editor/UnauthorizedApiKeyModal";
import { OptiSignsAccountChoiceDialog } from "@/components/editor/optisigns-account-choice-dialog";
import { Badge } from "@/components/ui/badge";
import { InsufficientBalanceModal } from "@/components/billing/InsufficientBalanceModal";

import { Deployment, DeployStatus } from "@/types/deployment";
import { ProjectVisibility, PlanType } from "@optidev/shared-types";
import { useAgentUIStore } from "@/stores/agentUIStore";
import { billingApi } from "@/lib/api/billing";
import { usePlanFeatures } from "@/hooks/useFeatures";
import { useProjectStore } from "@/stores/projectStore";
import { useAgentChatStore } from "@/stores/agentChatStore";
import { auth, firebaseAuth } from "@/lib/firebase";
import { deploymentsApi } from "@/lib/api/deployments";
import { markStepIfNeeded } from "@/lib/api/onboarding";
import { workspaceKeysApi } from "@/lib/api/workspace-keys";
import { customDomainsApi, CustomDomain } from "@/lib/api/custom-domains";
import { useOptiSignsPicker } from "@/hooks/use-optisigns-picker";
import { useOptiSignsConnect } from "@/hooks/use-optisigns-connect";
import { OptisignsPickerSdk } from "@optisigns/picker-sdk";
import { usePermissions } from "@/hooks/usePermissions";
import { useEmbedMode } from "@/hooks/useEmbedMode";
import { getActiveBridge, CHILD_ACTION } from "@/embed/OptiSignsBridge";
import { debuggingApi } from "@/lib/api/supabase";
import { supabaseApi } from "@/lib/api/supabase";
import { commitsApi, CheckChangesResponse } from "@/lib/api/commits";
import type { SupabaseLint } from "@optidev/shared-types";

const API_URL = import.meta.env.VITE_API_URL;

export type BuildStatus =
  | "queued"
  | "running"
  | "success"
  | "failed"
  | "timeout"
  | "cancelled";

interface PublishDropdownProps {
  projectId?: string;
  disabled?: boolean;
  trigger?: React.ReactNode;
  onNavigateToSettings?: (section: string) => void;
  /** Compact mode for mobile - smaller padding/font, shorter text */
  compact?: boolean;
  /** Mobile settings opener - opens settings drawer on mobile */
  onMobileOpenSettings?: (section?: 'info' | 'deploy' | 'optidev-cloud' | 'agent-settings') => void;
}

export function PublishDropdown({
  projectId,
  disabled = false,
  trigger,
  onNavigateToSettings,
  compact = false,
  onMobileOpenSettings,
}: PublishDropdownProps) {
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildStatus, setBuildStatus] = useState<BuildStatus | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [currentBuildId, setCurrentBuildId] = useState<string | null>(null);
  const [latestDeployment, setLatestDeployment] = useState<Deployment | null>(
    null,
  );
  const [deploymentStatus, setDeploymentStatus] = useState<DeployStatus | null>(
    null,
  );
  const [showCustomDomainModal, setShowCustomDomainModal] = useState(false);
  const project = useProjectStore((state) => state.currentProject);
  const publishState = useProjectStore((state) => state.publishState);
  const { setCurrentProject, setIsPublishing } = useProjectStore();
  const [customDomains, setCustomDomains] = useState<CustomDomain[]>([]);
  const [showPublishRequiredModal, setShowPublishRequiredModal] =
    useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [showCommitModal, setShowCommitModal] = useState(false);
  const [commitCheckResult, setCommitCheckResult] =
    useState<CheckChangesResponse | null>(null);
  const [commitModalSource, setCommitModalSource] = useState<
    "publish" | "optisigns" | null
  >(null);
  const [isCheckingChanges, setIsCheckingChanges] = useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [pendingOptiSignsFlow, setPendingOptiSignsFlow] = useState(false);
  const [showAccountChoice, setShowAccountChoice] = useState(false);
  const [isCheckingKey, setIsCheckingKey] = useState(false);
  const { connectAndGetToken, isConnecting } = useOptiSignsConnect();
  const { openPicker } = useOptiSignsPicker({
    projectId: projectId,
    workspaceId: project?.workspaceId,
    publishedUrl: latestDeployment?.publicUrl,
    slug: project?.urlSlug,
    name: project?.name,
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { planFeatures } = usePlanFeatures();
  const [planType, setPlanType] = useState<PlanType>(PlanType.FREE);
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);

  const { isAdminOrOwner } = usePermissions();
  const isAgentRunning = useAgentUIStore((state) => state.isAgentStreaming);
  const { isEmbedded, embedSource } = useEmbedMode();
  const isOptiSignsEmbed = isEmbedded && embedSource === 'optisigns';

  // Unpublished changes tracking - uses commit SHA comparison (not timestamps)
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const [publishedCommitSha, setPublishedCommitSha] = useState<string | null>(
    null,
  );
  const [currentCommitSha, setCurrentCommitSha] = useState<string | null>(null);
  const latestAutoCommit = useAgentChatStore((s) => s.latestAutoCommit);

  // Snapshot of the SHA being published — set when a publish starts (either dropdown
  // or HITL path) and applied to publishedCommitSha on success. Avoids the race where
  // loadPublishedCommitSha() fires before the backend has marked the new deploy as current.
  const publishingCommitShaRef = useRef<string | null>(null);
  const prevPublishStatusRef = useRef<string>("idle");

  // Pulse animation on commit (brief attention grab)
  const [showPulse, setShowPulse] = useState(false);

  // Advisor issues state
  const [advisorIssues, setAdvisorIssues] = useState<{
    security: SupabaseLint[];
    performance: SupabaseLint[];
  }>({ security: [], performance: [] });
  const [hasSupabase, setHasSupabase] = useState(false);

  // Check if custom domain is allowed for current plan
  const currentPlanFeatures = planFeatures[planType] || planFeatures.free;
  const canUseCustomDomain = currentPlanFeatures.customDomain;

  // Fetch subscription to get plan type and cloud billing status
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const token = await auth.getIdToken();
        if (token) {
          const subscription = await billingApi.getSubscription(token);
          setPlanType(subscription.planType);
        }
      } catch (error) {
        console.error("Failed to fetch subscription:", error);
        setPlanType(PlanType.FREE);
      }
    };
    fetchPlan();
  }, []);

  // Fetch advisor issues on mount (if Supabase is active)
  useEffect(() => {
    const fetchAdvisorIssues = async () => {
      if (!projectId) return;

      try {
        const token = await auth.getIdToken();
        if (!token) return;

        // First check if Supabase is activated for this project
        const status = await supabaseApi.getProjectStatus(token, projectId);
        const isActive = status.status === "ACTIVE_HEALTHY";
        setHasSupabase(isActive);

        if (!isActive) {
          setAdvisorIssues({ security: [], performance: [] });
          return;
        }

        // Fetch advisor issues in parallel
        const [securityResponse, performanceResponse] = await Promise.all([
          debuggingApi.getAdvisors(token, projectId, "security"),
          debuggingApi.getAdvisors(token, projectId, "performance"),
        ]);

        setAdvisorIssues({
          security: securityResponse.lints || [],
          performance: performanceResponse.lints || [],
        });
      } catch (error) {
        // Silently fail - don't block publish if advisors fail to load
        console.error("Failed to fetch advisor issues:", error);
      }
    };

    fetchAdvisorIssues();
  }, [projectId]);

  // Calculate issues by severity
  const allIssues = [...advisorIssues.security, ...advisorIssues.performance];
  const errorCount = allIssues.filter((i) => i.level === "ERROR").length;
  const warnCount = allIssues.filter((i) => i.level === "WARN").length;
  const infoCount = allIssues.filter((i) => i.level === "INFO").length;
  const hasAdvisorWarnings = allIssues.length > 0;

  // Build summary text like "2 errors, 1 warning" or "3 suggestions"
  const getAdvisorSummary = () => {
    const parts: string[] = [];
    if (errorCount > 0)
      parts.push(`${errorCount} ${errorCount === 1 ? "error" : "errors"}`);
    if (warnCount > 0)
      parts.push(`${warnCount} ${warnCount === 1 ? "warning" : "warnings"}`);
    if (infoCount > 0)
      parts.push(
        `${infoCount} ${infoCount === 1 ? "suggestion" : "suggestions"}`,
      );
    return parts.join(", ");
  };

  // Helper to reset OptiSigns flow state
  const resetOptiSignsFlow = (errorMessage?: string): void => {
    setShowAccountChoice(false);
    setIsCheckingKey(false);
    setIsCheckingChanges(false);
    if (errorMessage) {
      toast.error(errorMessage);
    }
  };

  // Send PUBLISH_COMPLETE to scio3 parent (embed mode only)
  const sendPublishCompleteToParent = (openPushToScreens: boolean): void => {
    const bridge = getActiveBridge();
    if (!bridge) return;
    bridge.sendToParent(CHILD_ACTION.PUBLISH_COMPLETE, {
      projectId: projectId ?? '',
      deployUrl: latestDeployment?.publicUrl ?? '',
      projectName: project?.name ?? '',
      openPushToScreens,
    });
  };

  // Continue OptiSigns flow after publish - checks API key and shows account choice
  const continueOptiSignsFlow = async (): Promise<void> => {
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (isLocalhost) {
      setShowAccountChoice(true);
      return;
    }

    // Show modal immediately with loading state
    setShowAccountChoice(true);
    setIsCheckingKey(true);

    try {
      const token = await auth.getIdToken();
      if (!token) {
        resetOptiSignsFlow("Authentication required");
        return;
      }

      // Check if workspace has an active API key (available to all users)
      const { hasActiveKey } = await workspaceKeysApi.checkExists(token);

      setIsCheckingKey(false);

      if (hasActiveKey) {
        // Workspace has API key - show account options
        // Modal is already open, just stop loading
      } else {
        // No API key exists - close account choice and check user permission
        setShowAccountChoice(false);

        if (isAdminOrOwner) {
          // Admin/Owner can generate new key
          setShowApiKeyModal(true);
        } else {
          // Non-admin must ask admin to generate key
          setShowUnauthorizedModal(true);
        }
      }
    } catch (error) {
      console.error("Error in OptiSigns flow:", error);
      resetOptiSignsFlow("Failed to start OptiSigns flow");
    }
  };

  // Handle cross-account publish via popup login
  const handleCrossAccountPublish = async () => {
    setShowAccountChoice(false);
    const result = await connectAndGetToken();
    if (!result) return; // User closed popup without completing

    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      // Link the OptiSigns account for future SmallApp callbacks
      await axios.post(
        `${API_URL}/api/integrations/optisigns/link-account`,
        { linkedUserToken: result.accessToken },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Open picker with the cross-account token
      const sdk = new OptisignsPickerSdk({
        graphqlEndpoint:
          import.meta.env.VITE_OPTISIGNS_GRAPHQL_ENDPOINT ||
          "https://qa2-api-gateway.optisigns.com/graphql",
        pickerUrl:
          import.meta.env.VITE_OPTISIGNS_PICKER_URL ||
          "https://optisigns-picker-sdk.pages.dev",
        accessToken: result.accessToken,
        payload: {
          appType: "optidev",
          originalFileName: project?.urlSlug,
          projectName: project?.name,
          projectId: projectId,
          workspaceId: project?.workspaceId,
          url: latestDeployment?.publicUrl,
        },
        mediaType: "asset",
        modalMode: "wide",
        branding: {
          primaryColor: "#5C41E6",
          secondaryColor: "#5C41E6",
          iconType: "optidev",
        },
      });
      await sdk.openModalAsync();
    } catch (error) {
      console.error("Cross-account publish failed:", error);
      toast.error("Failed to publish to OptiSigns");
    }
  };

  // Check for uncommitted changes before embed Push to Screens flow
  const checkChangesAndSendToParent = async (): Promise<void> => {
    if (!projectId) return;

    setIsCheckingChanges(true);
    try {
      const result = await commitsApi.checkForChanges(projectId);

      if (result.hasChanges) {
        // Show commit modal - track source so callbacks know to continue
        setCommitModalSource("optisigns");
        setCommitCheckResult(result);
        setShowCommitModal(true);
      } else {
        // No changes - send directly to parent
        sendPublishCompleteToParent(true);
      }
    } catch {
      // On error, proceed anyway (don't block)
      sendPublishCompleteToParent(true);
    } finally {
      setIsCheckingChanges(false);
    }
  };

  // Handle Publish to OptiSigns with pre-checks
  const handlePublishToOptiSigns = async (): Promise<void> => {
    // Embed mode: skip picker SDK, send PUBLISH_COMPLETE to scio3 parent
    if (isOptiSignsEmbed) {
      if (!latestDeployment?.publicUrl || deploymentStatus !== "success") {
        setPendingOptiSignsFlow(true);
        setShowPublishRequiredModal(true);
        return;
      }
      await checkChangesAndSendToParent();
      return;
    }

    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    // Skip all checks on localhost - go directly to picker
    // if (isLocalhost) {
    //   openPicker();
    //   return;
    // }

    // Production checks below...
    // 1. Check if project is published
    if (!latestDeployment?.publicUrl || deploymentStatus !== "success") {
      setPendingOptiSignsFlow(true); // Mark that we want to continue after publish
      setShowPublishRequiredModal(true);
      return;
    }

    // Open modal immediately with loading state
    setShowAccountChoice(true);

    // 2. Check for uncommitted changes before sending to OptiSigns
    // (so user can publish latest changes first)
    // Note: Don't set pendingOptiSignsFlow here - it will be set in modal callback
    // if user decides to publish. Otherwise useEffect triggers immediately.
    await checkChangesAndPublishForOptiSigns();
  };

  // Load latest deployment and custom domains on mount and when urlSlug changes (e.g., after rename)
  useEffect(() => {
    if (projectId) {
      loadLatestDeployment();
      loadCustomDomains();
      // Fetch both published and current commit SHAs for change detection
      loadPublishedCommitSha();
      loadCurrentCommitSha();
    }
  }, [projectId, project?.urlSlug]);

  // Fetch the published commit SHA (from getCurrentDeployment which only returns PUBLISH type)
  const loadPublishedCommitSha = async () => {
    if (!projectId) return;
    try {
      const currentDeploy =
        await deploymentsApi.getCurrentDeployment(projectId);
      if (currentDeploy?.commitSha) {
        setPublishedCommitSha(currentDeploy.commitSha);
      }
    } catch (error) {
      // No published deployment yet - that's fine
      console.debug("No published deployment found:", error);
    }
  };

  // Fetch the current (latest) commit SHA from the database
  // This persists across page refreshes unlike latestAutoCommit which is in-memory
  const loadCurrentCommitSha = async () => {
    if (!projectId) return;
    try {
      const commits = await commitsApi.listCommits(projectId, 1);
      if (commits.length > 0 && commits[0].commitSha) {
        setCurrentCommitSha(commits[0].commitSha);
      }
    } catch (error) {
      // No commits yet - that's fine
      console.debug("No commits found:", error);
    }
  };

  // Watch for publish completion and continue OptiSigns flow
  useEffect(() => {
    if (
      pendingOptiSignsFlow &&
      deploymentStatus === "success" &&
      latestDeployment?.publicUrl
    ) {
      setPendingOptiSignsFlow(false);
      if (isOptiSignsEmbed) {
        sendPublishCompleteToParent(true);
      } else {
        continueOptiSignsFlow();
      }
    }
  }, [pendingOptiSignsFlow, deploymentStatus, latestDeployment?.publicUrl, isOptiSignsEmbed]);

  // Capture the SHA being published when a HITL publish starts.
  // Dropdown path captures in executePublish() directly.
  useEffect(() => {
    const prev = prevPublishStatusRef.current;
    const curr = publishState.status;
    prevPublishStatusRef.current = curr;

    if (publishState.projectId !== projectId) return;

    const wasActive =
      prev !== "idle" && prev !== "success" && prev !== "failed";
    const isActive =
      curr !== "idle" && curr !== "success" && curr !== "failed";

    if (!wasActive && isActive) {
      publishingCommitShaRef.current = currentCommitSha;
    }
  }, [publishState.status, publishState.projectId, projectId, currentCommitSha]);

  // On publish success from either path, apply the captured SHA locally.
  // Falls back to a server fetch if the ref is empty (cross-tab, page reload, etc.).
  useEffect(() => {
    if (!projectId) return;
    const localSuccess = deploymentStatus === "success";
    const hitlSuccess =
      publishState.status === "success" &&
      publishState.projectId === projectId;
    if (!localSuccess && !hitlSuccess) return;

    if (publishingCommitShaRef.current) {
      setPublishedCommitSha(publishingCommitShaRef.current);
      publishingCommitShaRef.current = null;
    } else {
      loadPublishedCommitSha();
    }
  }, [deploymentStatus, publishState.status, publishState.projectId, projectId]);

  // Update currentCommitSha when a new auto-commit fires during this session
  useEffect(() => {
    if (latestAutoCommit?.commitSha) {
      setCurrentCommitSha(latestAutoCommit.commitSha);
    }
  }, [latestAutoCommit?.commitSha]);

  // Detect unpublished changes - compare current commit SHA with published commit SHA
  useEffect(() => {
    if (!publishedCommitSha) {
      // Never published - show "Publish" not "Sync Changes"
      setHasUnpublishedChanges(false);
      return;
    }
    if (!currentCommitSha) {
      // No commits yet - nothing to sync
      setHasUnpublishedChanges(false);
      return;
    }
    // Compare SHAs: different = unpublished changes, same = in sync
    setHasUnpublishedChanges(currentCommitSha !== publishedCommitSha);
  }, [currentCommitSha, publishedCommitSha]);

  // Trigger pulse animation on any new commit (brief attention grab)
  useEffect(() => {
    if (latestAutoCommit?.status === "created" && latestAutoCommit?.timestamp) {
      setShowPulse(true);
      const timer = setTimeout(() => setShowPulse(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [latestAutoCommit?.timestamp]);

  const loadCustomDomains = async () => {
    if (!projectId) return;

    try {
      const domains = await customDomainsApi.list(projectId);
      setCustomDomains(domains);
    } catch (error) {
      console.error("Error loading custom domains:", error);
    }
  };

  // Poll for build status
  useEffect(() => {
    if (!currentBuildId || !projectId) return;

    const pollStatus = async () => {
      try {
        const token = await auth.getIdToken();
        const response = await axios.get(
          `${API_URL}/api/projects/${projectId}/builds/${currentBuildId}/status`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const { status, message } = response.data;
        setBuildStatus(status);

        if (status === "failed" || status === "timeout") {
          setBuildError(message || "Build failed");
          setIsBuilding(false);
          setIsPublishing(false); // Allow rename again on build failure
          setCurrentBuildId(null);
        } else if (status === "success") {
          // Don't set isBuilding to false yet - keep button disabled until deployment starts
          setCurrentBuildId(null);
          setBuildStatus(null); // Clear build status immediately
          // Poll for deployment after build success
          pollForDeployment();
        }
      } catch (error) {
        console.error("Error polling build status:", error);
      }
    };

    // Poll every 2 seconds while building
    const interval = setInterval(pollStatus, 2000);

    // Initial poll
    pollStatus();

    return () => clearInterval(interval);
  }, [currentBuildId, projectId]);

  // Poll for deployment status
  useEffect(() => {
    if (
      !latestDeployment ||
      !projectId ||
      (deploymentStatus !== "queued" && deploymentStatus !== "running")
    ) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const status = await deploymentsApi.getDeploymentStatus(
          projectId,
          latestDeployment.id,
        );
        setDeploymentStatus(status.status);

        if (status.status === "success") {
          setIsBuilding(false);
          setIsPublishing(false); // Allow rename again on success
          setLatestDeployment((prev) =>
            prev ? { ...prev, publicUrl: status.publicUrl } : null,
          );

          // Update project store with new publish URL (keeps store in sync)
          if (status.publicUrl && project) {
            setCurrentProject({
              ...project,
              latestPublishUrl: status.publicUrl,
            });
          }

          // Fire-and-forget onboarding step 3 mark + analytics (PostHog + GTM)
          // Analytics only fire when backend confirms marked === true (cross-browser dedup)
          const token = await auth.getIdToken();
          const uid = firebaseAuth.currentUser?.uid;
          if (token && uid) markStepIfNeeded('step3', token, uid, { projectId });

          // Get display URL (custom domain if primary, otherwise default)
          const primaryDomain = customDomains.find(
            (d) => d.isPrimary && d.status === "active",
          );
          const displayUrl = primaryDomain
            ? `https://${primaryDomain.domain}`
            : status.publicUrl;

          // Show clean success toast with URL
          toast.success(
            <div className="flex flex-col gap-0.5 min-w-0 max-w-[250px]">
              <span className="text-sm font-medium">
                Deployed successfully!
              </span>
              <a
                href={displayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:text-blue-300 underline block truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {displayUrl}
              </a>
            </div>,
            {
              duration: 8000,
            },
          );
          // Keep deployment status visible in dropdown (don't reset)
        } else if (status.status === "failed") {
          setIsPublishing(false); // Allow rename again on failure
          toast.error("Deployment failed", {
            duration: 4000,
            style: {
              background: "#18181b",
              color: "#fafafa",
              border: "1px solid #27272a",
            },
          });
          // Reset status after 5 seconds
          setTimeout(() => {
            setDeploymentStatus(null);
          }, 5000);
        }
      } catch (error) {
        console.error("Error polling deployment status:", error);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [latestDeployment, deploymentStatus, projectId]);

  const loadLatestDeployment = async () => {
    if (!projectId) return;

    try {
      // Only fetch publish deployments (not previews)
      const deployments = await deploymentsApi.getDeployments(
        projectId,
        1,
        "publish",
      );
      if (deployments.length > 0) {
        const latestPublished = deployments[0];
        setLatestDeployment(latestPublished);
        setDeploymentStatus(latestPublished.status);

        // If already succeeded, clear building state and ensure step3 is marked
        if (latestPublished.status === "success") {
          setIsBuilding(false);

          // Fire-and-forget onboarding step 3 mark + analytics (PostHog + GTM)
          // Safety net: catches missed polling, fast publish, page refresh, HITL publish
          // No-op if already marked (localStorage dedup on first line of markStepIfNeeded)
          const token = await auth.getIdToken();
          const uid = firebaseAuth.currentUser?.uid;
          if (token && uid) markStepIfNeeded('step3', token, uid, { projectId });
        }
      }
    } catch (error) {
      console.error("Error loading deployment:", error);
    }
  };

  const pollForDeployment = async () => {
    // Wait a bit for auto-deploy to start
    setTimeout(() => {
      loadLatestDeployment();
    }, 2000);
  };

  // Refresh deployment data when store's publish succeeds (HITL-triggered publish)
  // This ensures the dropdown shows the correct URL and status after HITL publish
  useEffect(() => {
    if (publishState.status === 'success') {
      loadLatestDeployment();
    }
  }, [publishState.status]);

  // Check for uncommitted changes before publishing
  // Shows modal if there are changes, otherwise proceeds directly
  const checkChangesAndPublish = async () => {
    if (!projectId) {
      console.error("No project ID available");
      return;
    }

    setIsCheckingChanges(true);
    try {
      const result = await commitsApi.checkForChanges(projectId);

      if (result.hasChanges) {
        // Show commit modal with the check result
        setCommitModalSource("publish");
        setCommitCheckResult(result);
        setShowCommitModal(true);
      } else {
        // No changes, proceed directly to publish
        executePublish();
      }
    } catch (error: any) {
      console.error("Failed to check for changes:", error);
      // On error, proceed with publish anyway (don't block)
      executePublish();
    } finally {
      setIsCheckingChanges(false);
    }
  };

  // Check for uncommitted changes before OptiSigns flow
  // If no changes, skip to OptiSigns directly (no re-publish needed)
  // If changes exist, show modal and publish first
  const checkChangesAndPublishForOptiSigns = async (): Promise<void> => {
    if (!projectId) {
      console.error("No project ID available");
      return;
    }

    setIsCheckingChanges(true);
    try {
      const result = await commitsApi.checkForChanges(projectId);

      if (result.hasChanges) {
        // Show commit modal - track source so callbacks know to continue to OptiSigns
        setShowAccountChoice(false); // Close account choice modal
        setCommitModalSource("optisigns");
        setCommitCheckResult(result);
        setShowCommitModal(true);
      } else {
        // No changes - skip publish, go directly to OptiSigns
        continueOptiSignsFlow();
      }
    } catch (error: any) {
      console.error("Failed to check for changes:", error);
      // On error, proceed to OptiSigns anyway (don't block)
      continueOptiSignsFlow();
    } finally {
      setIsCheckingChanges(false);
    }
  };

  // Handle commit modal callbacks
  const handleCommitAndPublish = () => {
    // If opened from OptiSigns flow, set flag so useEffect continues after publish
    if (commitModalSource === "optisigns") {
      setPendingOptiSignsFlow(true);
    }
    setShowCommitModal(false);
    setCommitCheckResult(null);
    setCommitModalSource(null);
    executePublish();
  };

  const handlePublishWithoutCommit = () => {
    // If opened from OptiSigns flow, set flag so useEffect continues after publish
    if (commitModalSource === "optisigns") {
      setPendingOptiSignsFlow(true);
    }
    setShowCommitModal(false);
    setCommitCheckResult(null);
    setCommitModalSource(null);
    executePublish();
  };

  const handleCloseCommitModal = () => {
    setShowCommitModal(false);
    setCommitCheckResult(null);
    setCommitModalSource(null);
  };

  // Show visibility modal only on first publish, then check for uncommitted changes
  const handlePublishClick = () => {
    // Only show visibility modal on first publish (no successful deployment yet)
    const hasBeenPublished =
      latestDeployment?.publicUrl && deploymentStatus === "success";
    if (hasBeenPublished) {
      // Re-publish - check for uncommitted changes first
      checkChangesAndPublish();
    } else {
      // First time publish - show visibility modal first
      setShowVisibilityModal(true);
    }
  };

  // Handle visibility selection and proceed with publish
  const handlePublishWithVisibility = async (visibility: ProjectVisibility) => {
    if (!projectId) {
      console.error("No project ID available");
      return;
    }

    setIsUpdatingVisibility(true);

    try {
      // Update visibility if it changed
      if (visibility !== project?.visibility) {
        const token = await auth.getIdToken();
        if (!token) {
          toast.error("Authentication required");
          setIsUpdatingVisibility(false);
          return;
        }

        const response = await axios.put(
          `${API_URL}/api/editor/projects/${projectId}`,
          { visibility },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Update the project in the store
        setCurrentProject(response.data);
      }

      // Close modal
      setShowVisibilityModal(false);
      setIsUpdatingVisibility(false);

      // Check for uncommitted changes before publishing
      await checkChangesAndPublish();
    } catch (error: any) {
      console.error("Failed to update visibility:", error);
      toast.error("Failed to update visibility");
      setIsUpdatingVisibility(false);
    }
  };

  // Execute the actual publish/build process
  // Uses smart publish endpoint: fast-publish if preview exists, else full build
  const executePublish = async () => {
    if (!projectId) {
      console.error("No project ID available");
      return;
    }

    publishingCommitShaRef.current = currentCommitSha;

    // Reset status when starting
    setDeploymentStatus(null);
    setIsBuilding(true);
    setIsPublishing(true); // Block rename during publish
    setBuildStatus("queued");
    setBuildError(null);
    setHasUnpublishedChanges(false); // Reset immediately on publish

    try {
      const token = await auth.getIdToken();

      // Use smart publish endpoint - uses fast-publish if preview is up-to-date
      const response = await axios.post(
        `${API_URL}/api/projects/${projectId}/builds/publish`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const { fastPublish, deployId, buildId, message } = response.data;
      console.log("[Publish] Response:", response.data);

      if (fastPublish) {
        // Fast publish completed immediately - deployment is done
        console.log("[Publish] Fast publish completed:", message);
        setBuildStatus(null);
        setIsBuilding(false);
        setIsPublishing(false); // Allow rename again
        // Reload to show the new deployment
        loadLatestDeployment();
      } else {
        // Full build started - poll for status
        console.log("[Publish] Full build started:", message);
        setCurrentBuildId(buildId);
        setBuildStatus("running");
      }
    } catch (error: any) {
      setIsBuilding(false);
      setIsPublishing(false); // Allow rename again on failure

      // Handle 402 Payment Required - insufficient balance
      if (error.response?.status === 402) {
        setBuildStatus(null);
        setShowInsufficientBalanceModal(true);
        return;
      }

      setBuildStatus("failed");
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to start build";
      setBuildError(errorMessage);
      console.error("Build failed:", error);

      // Reset status after 5 seconds
      setTimeout(() => {
        setBuildStatus(null);
        setBuildError(null);
      }, 5000);
    }
  };

  const handleCustomDomain = () => {
    if (!canUseCustomDomain) {
      navigate("/dashboard/settings/billing");
      return;
    }
    setShowCustomDomainModal(true);
  };

  // Get button icon based on deployment and build status
  const getButtonIcon = () => {
    // Check store's publishState first (for HITL-triggered publishes)
    const storeStatus = publishState.status;
    if (storeStatus === 'queued' || storeStatus === 'building' || storeStatus === 'deploying') {
      return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    }
    if (storeStatus === 'failed') {
      return <XCircle className="w-3.5 h-3.5 text-red-400" />;
    }
    // Note: 'success' falls through to local state (refresh updates deploymentStatus)

    // Loading states (local)
    if (
      deploymentStatus === "running" ||
      deploymentStatus === "queued" ||
      isBuilding
    ) {
      return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    }

    // Error states
    if (
      deploymentStatus === "failed" ||
      buildStatus === "failed" ||
      buildStatus === "timeout"
    ) {
      return <XCircle className="w-3.5 h-3.5 text-red-400" />;
    }

    // Published states
    if (deploymentStatus === "success") {
      if (hasUnpublishedChanges) {
        return <Upload className="w-3.5 h-3.5" />; // Sync changes
      }
      return null; // No left icon when published & in sync (visibility icon shows status)
    }

    // Initial state - never published
    return <Rocket className="w-3.5 h-3.5" />;
  };

  // Get button text based on status
  const getButtonText = () => {
    // Check store's publishState first (for HITL-triggered publishes)
    const storeStatus = publishState.status;
    if (storeStatus === 'queued') return "Queuing...";
    if (storeStatus === 'building') return "Building...";
    if (storeStatus === 'deploying') return "Deploying...";
    if (storeStatus === 'failed') return "Build Failed";
    // Note: 'success' falls through to local state (refresh updates deploymentStatus)

    // Check deployment status first (local)
    if (deploymentStatus === "running" || deploymentStatus === "queued") {
      return "Deploying...";
    }
    if (deploymentStatus === "success") {
      if (hasUnpublishedChanges) {
        return compact ? "Sync" : "Sync Changes";
      }
      return "Published!";
    }
    if (deploymentStatus === "failed") {
      return "Deploy Failed";
    }

    // Then check build status
    if (isBuilding) {
      if (buildStatus === "queued") return "Queuing...";
      if (buildStatus === "running") return "Building...";
      return "Building...";
    }
    if (buildStatus === "failed" || buildStatus === "timeout")
      return "Build Failed";

    return "Publish";
  };

  // Get visibility icon with status-based color
  // Only show after site has been published and not during loading states
  const getVisibilityIcon = () => {
    // Don't show during loading states
    if (
      deploymentStatus === "running" ||
      deploymentStatus === "queued" ||
      isBuilding
    ) {
      return null;
    }

    // Don't show visibility icon if never published
    if (!latestDeployment?.publicUrl && deploymentStatus !== "success") {
      return null;
    }

    // Determine color based on status
    let colorClass = "text-muted-foreground"; // default

    if (
      deploymentStatus === "failed" ||
      buildStatus === "failed" ||
      buildStatus === "timeout"
    ) {
      colorClass = "text-red-400"; // error state
    } else if (deploymentStatus === "success") {
      if (hasUnpublishedChanges) {
        colorClass = "text-blue-400"; // needs sync
      } else {
        colorClass = "text-green-400"; // all good
      }
    }

    // Show lock for private, globe for public/default
    if (project?.visibility === ProjectVisibility.PRIVATE) {
      return <Lock className={`w-3.5 h-3.5 ${colorClass}`} />;
    }
    return <Globe className={`w-3.5 h-3.5 ${colorClass}`} />;
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {trigger || (
            <Button
              variant="ghost"
              size="sm"
              disabled={
                disabled ||
                isBuilding ||
                deploymentStatus === "running" ||
                deploymentStatus === "queued" ||
                (publishState.status !== 'idle' && publishState.status !== 'success' && publishState.status !== 'failed')
              }
              className={`group gap-1.5 text-foreground rounded-md transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_2px_8px_rgba(0,0,0,0.12)] dark:shadow-[0_2px_8px_rgba(255,255,255,0.08)] ${compact ? "h-7 px-2" : "h-8 px-3"} ${showPulse ? "publish-shimmer" : ""} ${hasUnpublishedChanges ? "bg-blue-500/15 hover:bg-blue-500/25 border border-blue-400" : "bg-muted/50 hover:bg-muted border border-zinc-300 dark:border-zinc-600"}`}
            >
              {getButtonIcon() && (
                <div className="relative flex items-center">
                  {getButtonIcon()}
                  {hasUnpublishedChanges && deploymentStatus === "success" && (
                    <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                  )}
                </div>
              )}
              <span className={`font-medium ${compact ? "text-xs" : "text-sm"}`}>{getButtonText()}</span>
              {getVisibilityIcon()}
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 border-border/30">
          {/* Live Site URLs - Use latestDeployment.publicUrl (immediate) or project.latestPublishUrl (from store) */}
          {(latestDeployment?.publicUrl || project?.latestPublishUrl) &&
            deploymentStatus === "success" && (
              <div className="px-3 py-3 border-b border-border/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Live Site
                  </span>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px] px-2 py-0.5 font-semibold">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5 animate-pulse" />
                    LIVE
                  </Badge>
                </div>

                {/* Primary Custom Domain (if exists) */}
                {(() => {
                  const primaryDomain = customDomains.find(
                    (d) => d.isPrimary && d.status === "active",
                  );
                  if (primaryDomain) {
                    return (
                      <a
                        href={`https://${primaryDomain.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors group mb-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Globe className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate flex-1 font-medium">
                          {primaryDomain.domain}
                        </span>
                        <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </a>
                    );
                  }
                  return null;
                })()}

                {/* Deployment URL - prefer project.latestPublishUrl (updated on rename), fallback to latestDeployment.publicUrl */}
                {(() => {
                  const liveUrl =
                    project?.latestPublishUrl || latestDeployment?.publicUrl;
                  if (!liveUrl) return null;
                  return (
                    <a
                      href={liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors group"
                    >
                      <Globe className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate flex-1 font-medium">
                        {liveUrl.replace("https://", "")}
                      </span>
                      <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                    </a>
                  );
                })()}
              </div>
            )}

          {/* Deployment in progress */}
          {deploymentStatus === "running" && (
            <div className="px-3 py-3 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                <span className="text-sm text-blue-400 font-medium">
                  Deploying to production...
                </span>
              </div>
            </div>
          )}

          <DropdownMenuItem
            onClick={handlePublishClick}
            className={`gap-3 cursor-pointer py-3 hover:bg-accent/50 ${hasUnpublishedChanges ? "bg-blue-500/5" : ""}`}
            disabled={isBuilding || isCheckingChanges || isAgentRunning}
          >
            {isBuilding || isCheckingChanges ? (
              <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
            ) : hasUnpublishedChanges ? (
              <div className="relative">
                <Upload className="w-4 h-4 text-blue-400" />
                <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-blue-400 rounded-full" />
              </div>
            ) : (
              <Upload className="w-4 h-4 text-muted-foreground" />
            )}
            <div className="flex-1 min-w-0">
              <span className="font-medium">
                {isCheckingChanges
                  ? "Checking..."
                  : isBuilding
                    ? "Building..."
                    : hasUnpublishedChanges
                      ? "Sync Changes"
                      : deploymentStatus === "success"
                        ? "Republish"
                        : "Publish"}
              </span>
              {hasUnpublishedChanges && !isCheckingChanges && !isBuilding && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Your live site is not updated yet
                </p>
              )}
            </div>
            {buildError && (
              <span className="text-xs text-destructive ml-2 font-semibold">
                Failed
              </span>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handlePublishToOptiSigns}
            className="gap-3 cursor-pointer py-3 hover:bg-accent/50"
            disabled={isAgentRunning}
          >
            <img
              src="/optisigns-logo.svg"
              alt="OptiSigns"
              className="w-4 h-4 rounded-sm"
            />
            <span className="font-medium">{isOptiSignsEmbed ? "Push to Screens" : "Publish to OptiSigns"}</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={handleCustomDomain}
            className="gap-3 cursor-pointer py-3 hover:bg-accent/50"
          >
            <Globe className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium flex-1">Custom Domain</span>
            {!canUseCustomDomain && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-500">
                <Sparkles className="h-3 w-3" />
                Upgrade
              </span>
            )}
          </DropdownMenuItem>

          {/* Advisor Warning - shown when there are security/performance issues */}
          {hasSupabase && hasAdvisorWarnings && (
            <DropdownMenuItem
              className="px-3 py-2.5 border-t border-border/50 rounded-none cursor-pointer"
              onSelect={() => {
                // On mobile, open the settings drawer directly
                if (onMobileOpenSettings) {
                  onMobileOpenSettings("optidev-cloud");
                  // Also set URL params so the advisors tab is selected
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set("cloudTab", "advisors");
                  setSearchParams(newParams);
                  return;
                }
                // Desktop: Set viewMode to "settings" first to prevent the cleanup useEffect from stripping params
                if (onNavigateToSettings) {
                  onNavigateToSettings("optidev-cloud");
                }
                const newParams = new URLSearchParams(searchParams);
                newParams.set("tab", "settings");
                newParams.set("section", "optidev-cloud");
                newParams.set("cloudTab", "advisors");
                setSearchParams(newParams);
              }}
            >
              <div className="flex items-start gap-2">
                <AlertTriangle
                  className={`w-4 h-4 flex-shrink-0 mt-0.5 ${errorCount > 0 ? "text-red-500" : "text-amber-500"}`}
                />
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-xs font-medium ${errorCount > 0 ? "text-red-500" : "text-amber-500"}`}
                  >
                    OptiDev Cloud: {getAdvisorSummary()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Review security & performance advisors before publishing.
                  </p>
                </div>
              </div>
            </DropdownMenuItem>
          )}

          {/* Visibility Status - shown after publish */}
          {deploymentStatus === "success" && project?.visibility && (
            <div className="px-3 py-2.5 border-t border-border/50">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {project.visibility === ProjectVisibility.PRIVATE ? (
                  <>
                    <Lock className="w-3.5 h-3.5 text-yellow-500" />
                    <span>Private app - Only accessible through OptiSigns</span>
                  </>
                ) : (
                  <>
                    <Globe className="w-3.5 h-3.5 text-blue-400" />
                    <span>Public app - Anyone with the link can view</span>
                  </>
                )}
              </div>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Custom Domain Modal */}
      {projectId && (
        <CustomDomainModal
          isOpen={showCustomDomainModal}
          projectId={projectId}
          onClose={() => setShowCustomDomainModal(false)}
          onDomainsChange={loadCustomDomains}
        />
      )}

      {/* Publish Required Modal (with integrated visibility selection) */}
      <PublishRequiredModal
        isOpen={showPublishRequiredModal}
        currentVisibility={project?.visibility}
        onClose={() => {
          setShowPublishRequiredModal(false);
          setPendingOptiSignsFlow(false); // Clear pending flow if user cancels
        }}
        onPublish={(visibility) => {
          setShowPublishRequiredModal(false); // Close modal but keep pendingOptiSignsFlow true
          handlePublishWithVisibility(visibility); // Directly publish with selected visibility
        }}
        isUpdating={isUpdatingVisibility}
      />

      {/* Publish Visibility Modal */}
      <PublishVisibilityModal
        isOpen={showVisibilityModal}
        currentVisibility={project?.visibility}
        onClose={() => setShowVisibilityModal(false)}
        onPublish={handlePublishWithVisibility}
        isUpdating={isUpdatingVisibility}
      />

      {/* Commit Before Publish Modal */}
      {projectId && commitCheckResult && (
        <CommitBeforePublishModal
          isOpen={showCommitModal}
          projectId={projectId}
          checkResult={commitCheckResult}
          onClose={handleCloseCommitModal}
          onCommitAndPublish={handleCommitAndPublish}
          onPublishWithoutCommit={handlePublishWithoutCommit}
          isPublishing={isBuilding}
        />
      )}

      {/* OptiSigns API Key Modal */}
      <OptiSignsApiKeyModal
        isOpen={showApiKeyModal}
        onClose={() => setShowApiKeyModal(false)}
        onSuccess={() => {
          setShowAccountChoice(true);
          setIsCheckingKey(false);
        }}
      />

      {/* Unauthorized API Key Modal */}
      <UnauthorizedApiKeyModal
        isOpen={showUnauthorizedModal}
        onClose={() => setShowUnauthorizedModal(false)}
      />

      {/* OptiSigns Account Choice Dialog */}
      <OptiSignsAccountChoiceDialog
        isOpen={showAccountChoice}
        onClose={() => {
          setShowAccountChoice(false);
          setIsCheckingKey(false);
          setIsCheckingChanges(false);
        }}
        onUseCurrentAccount={() => {
          setShowAccountChoice(false);
          setIsCheckingKey(false);
          setIsCheckingChanges(false);
          openPicker();
        }}
        onSignInToAnother={handleCrossAccountPublish}
        isConnecting={isConnecting}
        isCheckingKey={isCheckingKey}
        isCheckingChanges={isCheckingChanges}
      />

      {/* Insufficient Balance Modal */}
      <InsufficientBalanceModal
        isOpen={showInsufficientBalanceModal}
        onClose={() => setShowInsufficientBalanceModal(false)}
        context="publish"
      />
    </>
  );
}
